<template>
  <div class="settings col-10 col-lg-8">
    <h1 class="title">Settings</h1>
    <p class="description">Manage your account and preferences</p>
    
    <div class="form-group card p-4 mt-3">
      <h2 class="section-title mt-0">Profile</h2>
      
      <div class="name">
        <label for="profile-displayed-name">Displayed name</label>
        <input
          type="text"
          class="form-control"
          v-model="displayedName"
          id="profile-displayed-name"
        />
      </div>
      
      <div class="newsletter">
        <div class="form-check">
          <input
            id="email-consent-newsletter"
            class="form-check-input"
            type="checkbox"
            v-model="newsletterConsent"
          />
          <label class="form-check-label" for="email-consent-newsletter">
            Subscribe to monthly newsletter emails
          </label>
          <p class="help-text mb-0">Only GanttProject Cloud news. No spam.</p>
        </div>
        <button
          id="btn-update-user-info"
          class="btn btn-attention"
          v-on:click="onUpdate"
        >
          <div class="spinner-wrapper">
            <i class="fas fa-spinner"></i>
          </div>
          Update
        </button>
      </div>

      <h2 class="section-title">Data Control</h2>

      <form class="two-cols-section">
        <div class="label">
          <label class="mb-0">Data Export</label>
          <p class="help-text mb-0">
            You can export data from the teams owned by you as ZIP file.
          </p>
        </div>
        <div class="actions">
          <a
            class="btn btn-attention"
            :class="{ disabled: downloadStatus === 'wip' }"
            v-on:click.prevent="onDownload"
            role="button"
            >{{ downloadStatus === 'wip' ? 'Exporting...' : 'Start export' }}</a
          >
          <a
            class="btn btn-attention mt-2"
            id="archive-link"
            :href="archiveLink"
            v-if="downloadStatus === 'ready'"
            >Download ZIP</a
          >          
        </div>
        <div class="status">
          <div class="progress" v-if="downloadStatus === 'wip'">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              aria-valuenow="75"
              aria-valuemin="0"
              aria-valuemax="100"
              style="width: 100%"
            ></div>
          </div>
          <p
            class="help-text text-left mt-2 mb-0"
            v-show="downloadStatus === 'wip'"
          >
            You don't have to wait. If export process takes considerable time,
            we will send you a link by email.
          </p>
        </div>
      </form>

      <h2 class="section-title">Account</h2>
      <div class="two-cols-section">
        <div class="label">
          <label class="mb-0">Delete Account</label>
        </div>

        <div class="actions">
          <p v-if="accountDeleted" class="help-text">
            Your account was deleted.
          </p>
          <button
            v-if="!accountDeleted"
            class="btn btn-attention"
            id="btn-delete-account"
            v-on:click="onDeleteAccount"
            type="button"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
    <account-delete-modal ref="deleteAccountModal" />
  </div>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-facing-decorator';
import {DownloadStuff, UserUpdate} from '../Routes';
import {NEWSLETTER_CONSENT, UserDto} from '../Teams';
import {handleXhrError} from '../utils/Ui';
import AccountDeleteModal from './AccountDeleteModal.vue';

import $ from 'jquery';

type DownloadStatus = 'void' | 'wip' | 'ready';

@Component({
  components: { AccountDeleteModal }
})
export default class ProfilePane extends Vue {
  public displayedName: string = '';
  public newsletterConsent: boolean = false;
  public downloadStatus: DownloadStatus = 'void';
  public archiveLink: string = '#';
  public accountDeleted: boolean = false;
  public $refs: {
    deleteAccountModal: AccountDeleteModal
  }

  public setUser(dto: UserDto) {
    this.displayedName = dto.name;
    this.newsletterConsent = (dto.emailConsents & NEWSLETTER_CONSENT) !== 0;
    this.accountDeleted = !!dto.isRemoved;
  }

  public onUpdate() {
    const button = $('#btn-update-user-info').addClass('disabled');
    UserUpdate.invoke({
      displayedName: this.displayedName,
      emailConsents: this.newsletterConsent ? NEWSLETTER_CONSENT : 0,
    }).then(() => {
      // We just don't want flashing spinner, so we let it spin for a second
      // even if request completes ultra-fast.
      window.setTimeout( () => button.removeClass('disabled'), 1000);
    }).fail((jqXhr) => handleXhrError(jqXhr, 'can\'t update profile'));
  }
  public onDownload() {
    if (this.downloadStatus === 'wip') {
      return;
    }
    this.downloadStatus = 'wip';

    DownloadStuff.invoke(undefined)
        .fail( (jqXhr) => {
          this.downloadStatus = 'void';
          handleXhrError(jqXhr, 'can\'t download stuff');
        });
  }
  public onDeleteAccount() {
    this.$refs.deleteAccountModal.show();
  }
}
</script>

<style lang="scss" scoped>
@import "sass/components/variables";

.settings {
  margin: 0 2rem;
  padding-bottom: 2rem;
}

.title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0;
  border-bottom: 0;
}

.description {
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 1rem;
  color: $gray;
}

.section-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin: 2rem 0 0.8rem;
}

.newsletter {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 1.5rem;
}

.two-cols-section {
  display: flex;
  flex-wrap: wrap;
  align-items: start;
  row-gap: 1rem;

  .label {
    flex: 0 0 58%;
  }

  .actions {
    flex: 0 1 42%;
    text-align: right;
  }

  .status {
    flex: 0 1 100%;
  }
}

.btn-attention {
  position: relative;
  padding-left: 1.5em;
  padding-right: 1.5em;

  .spinner-wrapper {
    display: none;
    position: absolute;
    top: 0px;
    right: 3px;
    bottom: 0px;
    flex-direction: column;
    justify-content: center;
  }

  &.disabled {
    .spinner-wrapper {
      display: flex;

      @keyframes spinner-anim {
        0% {
          transform: initial;
        }
        100% {
          transform: rotate(360deg);
        }
      }
      .fa-spinner {
        animation: spinner-anim 1s infinite;
      }
    }
  }
}
</style>
